import type {
  BlogPosting,
  BreadcrumbList,
  FAQPage,
  ListItem,
  MedicalOrganization,
  Thing,
  WebPage,
  WebSite,
} from 'schema-dts';
import { getArticle } from '@/config/articles';
import { business } from '@/config/business';
import { faqsByPath } from '@/config/faq';
import { normalizePathname } from '@/config/seo';
import { seoMeta } from '@/config/seoMeta';

/** 1200x630 crop of the home hero photo (public/og-image.jpg): og:image, twitter:image, business image. */
export const OG_IMAGE_PATH = '/og-image.jpg';
export const OG_IMAGE_ALT = `${business.brandName}: chronic care management care team`;
/** The brand logo shown in the header (public/logo-nobg2.png, 467x186). */
export const LOGO_PATH = '/logo-nobg2.png';

/**
 * Business node type: MedicalOrganization (site brief). It is an Organization subtype, not a
 * LocalBusiness/Place, so it takes address, telephone and email but no geo or opening hours.
 */
type BusinessNode = MedicalOrganization;
type GraphNode = Exclude<Thing, string> | BusinessNode;

export type SchemaPayload = { '@context': 'https://schema.org'; '@graph': GraphNode[] };

function pageUrl(site: string, path: string): string {
  if (!path || path === '/') return site;
  return `${site}${path.startsWith('/') ? path : `/${path}`}`;
}

const absolute = (site: string, pathOrUrl: string) =>
  /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : pageUrl(site, pathOrUrl);

/** Title up to " | Brand" (or the whole title): the page's short name for breadcrumbs. */
function shortName(title: string): string {
  const pipe = title.indexOf(' | ');
  return pipe > 0 ? title.slice(0, pipe).trim() : title;
}

/** Home > [parent segments that are routes in seoMeta] > page. */
function buildBreadcrumb(path: string, name: string, site: string): BreadcrumbList | null {
  if (path === '/') return null;
  const items: ListItem[] = [{ '@type': 'ListItem', position: 1, name: 'Home', item: site }];
  const segments = path.split('/').filter(Boolean);
  segments.forEach((_, i) => {
    const p = `/${segments.slice(0, i + 1).join('/')}`;
    const last = i === segments.length - 1;
    const label = last ? name : seoMeta[p]?.title;
    if (label) items.push({ '@type': 'ListItem', position: items.length + 1, name: shortName(label), item: pageUrl(site, p) });
  });
  return { '@type': 'BreadcrumbList', itemListElement: items };
}

function buildOrganization(site: string, imageAbs: string): BusinessNode {
  return {
    '@type': 'MedicalOrganization',
    '@id': `${site}/#organization`,
    name: business.brandName,
    url: site,
    logo: pageUrl(site, LOGO_PATH),
    image: imageAbs,
    description: business.description,
    telephone: business.phoneE164,
    email: business.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.streetAddress,
      addressLocality: business.addressLocality,
      addressRegion: business.addressRegion,
      postalCode: business.postalCode,
      addressCountry: business.addressCountry,
    },
    ...(business.sameAs.length ? { sameAs: [...business.sameAs] } : {}),
  };
}

export function buildSchemaGraph(args: {
  path: string;
  title: string;
  description: string;
  siteUrl: string;
  imageUrl: string;
}): GraphNode[] {
  const path = normalizePathname(args.path);
  const site = args.siteUrl.replace(/\/+$/, '');
  const canonical = pageUrl(site, path);
  const organizationId = `${site}/#organization`;
  const websiteId = `${site}/#website`;

  const webPage: WebPage = {
    '@type': 'WebPage',
    '@id': canonical,
    url: canonical,
    name: args.title,
    description: args.description,
    inLanguage: 'en-US',
    isPartOf: { '@id': websiteId },
    about: { '@id': organizationId },
  };
  const webSite: WebSite = {
    '@type': 'WebSite',
    '@id': websiteId,
    url: site,
    name: business.brandName,
    inLanguage: 'en-US',
    publisher: { '@id': organizationId },
  };
  const graph: GraphNode[] = [buildOrganization(site, args.imageUrl), webPage, webSite];

  const article = getArticle(path);
  if (article) {
    const posting: BlogPosting = {
      '@type': 'BlogPosting',
      '@id': `${canonical}#article`,
      headline: article.headline,
      description: args.description,
      url: canonical,
      mainEntityOfPage: canonical,
      image: absolute(site, article.image ?? args.imageUrl),
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      publisher: { '@id': organizationId },
      author: article.author
        ? { '@type': 'Person', name: article.author.name, ...(article.author.jobTitle ? { jobTitle: article.author.jobTitle } : {}) }
        : { '@id': organizationId },
      inLanguage: 'en-US',
    };
    graph.push(posting);
  }

  const faqs = faqsByPath[path];
  if (faqs?.length) {
    const faqPage: FAQPage = {
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      isPartOf: { '@id': canonical },
      mainEntity: faqs.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
    };
    graph.push(faqPage);
  }

  const breadcrumb = buildBreadcrumb(path, article?.headline ?? args.title, site);
  if (breadcrumb) graph.push(breadcrumb);

  return graph;
}

export function buildSchemaPayload(args: Parameters<typeof buildSchemaGraph>[0]): SchemaPayload {
  return { '@context': 'https://schema.org', '@graph': buildSchemaGraph(args) };
}
